import { useState, useEffect, useCallback } from 'react';
import { supabase, Agent, AgentCategory } from '../lib/supabase';

export function useAgents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [categories, setCategories] = useState<AgentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAgents = useCallback(async (filters?: {
    categories?: string[];
    sources?: string[];
    search?: string;
  }) => {
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from('agents')
        .select('*')
        .order('stars', { ascending: false })
        .limit(100); // Add reasonable limit

      // Apply filters
      if (filters?.categories && filters.categories.length > 0) {
        query = query.in('category', filters.categories);
      }

      if (filters?.sources && filters.sources.length > 0) {
        query = query.in('source', filters.sources);
      }

      if (filters?.search && filters.search.trim()) {
        const searchTerm = filters.search.trim();
        query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,author.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Fetched agents:', data?.length || 0);
      setAgents(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
      setAgents([]); // Clear agents on error
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('agent_categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }
      
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchCategories(),
        fetchAgents()
      ]);
    };
    
    loadInitialData();
  }, [fetchCategories, fetchAgents]);

  return {
    agents,
    categories,
    loading,
    error,
    refetch: fetchAgents,
    fetchAgents
  };
}